import {
  getBodyCode,
  getErrorBody,
  getErrorMessage,
  getHttpStatus,
  logFeatureEvent,
  reportFeatureIssue,
  type ObservabilityScope,
} from "./shared";
import {
  isHeatmapDownloadAssetError,
  type HeatmapDownloadAssetReason,
} from "@/lib/utils/heatmapDownload";

type HeatmapAction = "add" | "remove" | "capture";
type Heatmap403Cause = "plan_limit" | "auth_bug";

/**
 * Cloudflare / gateway timeouts while the origin is still screenshotting a
 * customer page. 524 is Cloudflare's "A Timeout Occurred" (~100s origin).
 * 504 is the generic gateway timeout; 522/523 are Cloudflare origin-unreachable
 * variants; 408 is a request timeout.
 */
const SCREENSHOT_TIMEOUT_STATUSES = new Set([408, 504, 522, 523, 524]);

const SCREENSHOT_TIMEOUT_MESSAGE =
  /\b(?:HTTP(?: error! status:)?\s*(?:408|504|522|523|524))\b/i;

/**
 * Screenshot capture is a long-running origin job (headless browser). When it
 * exceeds the edge timeout the API returns Cloudflare HTML, which is expected
 * friction — not a frontend defect. Callers still toast so the user can retry.
 */
export const isHeatmapScreenshotTimeout = (error: unknown) => {
  const status = getHttpStatus(error);
  if (status !== null) return SCREENSHOT_TIMEOUT_STATUSES.has(status);

  return SCREENSHOT_TIMEOUT_MESSAGE.test(getErrorMessage(error));
};

type HeatmapScope = ObservabilityScope & { planName?: string };

type ReportHeatmap403Params = {
  error: unknown;
  action: HeatmapAction;
  userId?: string;
  email?: string;
  propertyId?: string;
  planTier?: string;
  planName?: string;
};

const classifyHeatmap403Cause = (error: unknown): Heatmap403Cause => {
  const code = getBodyCode(getErrorBody(error));
  if (code?.includes("limit")) return "plan_limit";
  return "auth_bug";
};

/**
 * A heatmap 403 is either the plan gate doing its job (expected — Log) or the
 * authorization layer misfiring for an entitled user (defect — Issue).
 */
export const reportHeatmap403 = ({
  error,
  action,
  userId,
  email,
  propertyId,
  planTier,
  planName,
}: ReportHeatmap403Params) => {
  const userKey = userId || "unknown-user";
  const cause = classifyHeatmap403Cause(error);
  const scope: ObservabilityScope = { propertyId, planTier, userId, email };

  if (cause === "plan_limit") {
    logFeatureEvent({
      feature: "heatmaps",
      event: "plan_limit_reached",
      level: "warn",
      message: "Heatmaps: plan limit reached",
      scope,
      dedupKey: `403:${userKey}:${action}`,
      attributes: {
        heatmap_action: action,
        http_status: 403,
        plan_name: planName ?? null,
      },
    });
    return;
  }

  reportFeatureIssue({
    feature: "heatmaps",
    error,
    action,
    message: `Heatmaps: unexpected 403 on ${action}`,
    scope,
    tags: { heatmap_403_cause: cause },
    context: { planName },
    dedupKey: `403:${userKey}:${action}`,
  });
};

export const logHeatmapPageAdded = (scope: HeatmapScope, pageUrl: string) =>
  logFeatureEvent({
    feature: "heatmaps",
    event: "page_added",
    scope,
    attributes: { page_url: pageUrl },
  });

export const logHeatmapPageRemoved = (scope: HeatmapScope, pageUrl: string) =>
  logFeatureEvent({
    feature: "heatmaps",
    event: "page_removed",
    scope,
    attributes: { page_url: pageUrl },
  });

export const logHeatmapScreenshotCaptured = (scope: HeatmapScope, pageUrl: string) =>
  logFeatureEvent({
    feature: "heatmaps",
    event: "screenshot_captured",
    scope,
    attributes: { page_url: pageUrl },
  });

/** "Test this button" / "Test this element" / "Test a shorter page" on Heatmaps. */
export const logHeatmapTestStarted = (
  scope: HeatmapScope,
  pageUrl: string,
  source: "page" | "element",
  mode: "clicks" | "scroll",
) =>
  logFeatureEvent({
    feature: "heatmaps",
    event: "test_started",
    scope,
    attributes: { page_url: pageUrl, heatmap_test_source: source, heatmap_mode: mode },
  });

const logHeatmapDownloadAssetFailed = (
  scope: HeatmapScope,
  pageUrl: string | undefined,
  reason: HeatmapDownloadAssetReason,
) =>
  logFeatureEvent({
    feature: "heatmaps",
    event: "download_asset_failed",
    level: "warn",
    message: "Heatmaps: screenshot export asset unavailable",
    scope,
    attributes: {
      page_url: pageUrl ?? null,
      heatmap_download_asset_reason: reason,
    },
  });

/**
 * Missing / HTML proxy bodies, CORS/fetch failures, and browser decode
 * failures are expected export friction (the screenshot may still render in
 * <img>). Unexpected download errors stay Issues.
 */
export const reportHeatmapDownloadFailure = (
  scope: HeatmapScope,
  error: unknown,
  pageUrl?: string,
) => {
  if (isHeatmapDownloadAssetError(error)) {
    logHeatmapDownloadAssetFailed(scope, pageUrl, error.reason);
    return;
  }

  reportHeatmapFailure(scope, "download", error, pageUrl);
};

export const reportHeatmapFailure = (
  scope: HeatmapScope,
  action: HeatmapAction | "load_data" | "download",
  error: unknown,
  pageUrl?: string,
) => {
  if (action === "capture" && isHeatmapScreenshotTimeout(error)) {
    logFeatureEvent({
      feature: "heatmaps",
      event: "screenshot_timeout",
      level: "warn",
      message: "Heatmaps: screenshot capture timed out",
      scope,
      attributes: {
        heatmap_action: action,
        http_status: getHttpStatus(error),
        page_url: pageUrl ?? null,
      },
    });
    return;
  }

  reportFeatureIssue({
    feature: "heatmaps",
    error,
    action,
    message: `Heatmaps: ${action} failed`,
    scope,
    context: { pageUrl },
  });
};
